/**
 * Categories context – single source of truth from backend (config/categories).
 * Fetched once via TanStack Query after authentication; shared; colors unified for quality list and drawing.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { refreshMeasurementDisplayMapFromCategories } from '../constants/measurementLabels';
import { setActiveMeasGeometry } from '../UI/ComponnentsUI/DrawingCanvas/utils/segmentationSaveUtils';
import type { CategoryDef, MeasGeometryContext } from '../helpers/api/config/config';
import {
  fetchCategories,
  buildLabelToMeasName,
  getMeasurementNameFromLabel as getMeasurementNameFromLabelConfig,
} from '../helpers/api/config/config';
import {
  buildCategoryIdToMeasLabel,
  buildCategoryIdToMeasNames,
  buildMeasGeometryContext,
  buildMeasLabelToCategoryId,
  buildPerimeterMeasLabels,
  getMaxStructuresForCategory,
} from '../helpers/api/config/measConfig';
import { useProfile } from '../helpers/api/auth/auth';
import { isSharedReportPath, isSignupPath } from '../publicPaths';

const OPACITY = 1;

/** Generate a stable, distinct color from a string (e.g. category code). No static maps – used only when DB has no default_color_rgba. */
function stableColorFromCode(code: string): string {
  let hash = 0;
  for (let i = 0; i < code.length; i++) {
    hash = code.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = Math.abs(hash) % 360;
  return `hsla(${h}, 72%, 52%, ${OPACITY})`;
}

export interface CategoriesContextValue {
  categories: CategoryDef[];
  /** code -> category id (for preset category_id and save) */
  categoriesByCode: Record<string, number>;
  /** category id -> code (for lookup by id) */
  codeById: Record<number, string>;
  /** MEAS__* -> category_id (from categories with meas_name; for segmentations) */
  measLabelToCategoryId: Record<string, number>;
  /** category_id -> all MEAS__* for that structure */
  categoryIdToMeasNames: Record<number, string[]>;
  /** category_id -> primary MEAS__* (first in meas_names) */
  categoryIdToMeasLabel: Record<number, string>;
  /** MEAS__* with value_type perimeter (from DB) */
  perimeterMeasLabels: Set<string>;
  /** Length/perimeter pair maps derived from category meas_names arrays */
  measGeometryContext: MeasGeometryContext;
  /** Category IDs that allow up to 2 structures per frame */
  categoryIdsWithMaxTwo: Set<number>;
  /** Resolve max_structures for a category from API (default 1). */
  getMaxStructuresForCategoryId: (id: number | null | undefined) => number;
  /** code -> css color (unified for quality list and drawing) */
  colorByCode: Record<string, string>;
  /** Get color by category id (preferred for processing – no label ambiguity). */
  getColorForCategoryId: (id: number | null | undefined) => string;
  /** Get color by category code (e.g. flag_code; preferred over label). */
  getColorForCode: (code: string | null | undefined) => string;
  /** Get color for a label: normalizes label to code then resolves (avoid for processing; use id/code). */
  getColorForLabel: (label: string | null | undefined) => string;
  /** Backend-driven: label/code -> MEAS__* (from category code + display_name). */
  labelToMeasName: Record<string, string>;
  /** Resolve label/code to measurement name (MEAS__*) from backend categories. */
  getMeasurementNameFromLabel: (label: string | null | undefined) => string | null;
  isLoading: boolean;
  isError: boolean;
}

const CategoriesContext = createContext<CategoriesContextValue | null>(null);

const CATEGORIES_QUERY_KEY = ['config', 'categories'] as const;

function normalizeToCode(label: string | null | undefined): string {
  if (!label || typeof label !== 'string') return '';
  return label
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_not_?visible$/gi, '');
}

export function CategoriesProvider({ children }: { children: React.ReactNode }) {
  // This provider mounts above the router, so it also runs on the patient share
  // page. Asking for a profile there is a guaranteed 401 for a visitor who has
  // no account, and nothing below this point is rendered for them anyway.
  const me = useProfile({
    retry: 0,
    enabled: !isSharedReportPath(window.location.pathname) && !isSignupPath(window.location.pathname),
  });
  const isAuthenticated = !!me?.data;

  const { data: categories = [], isLoading, isError } = useQuery({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: fetchCategories,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 60, // 1 hour – fetch once per session
    gcTime: 1000 * 60 * 60 * 2,
  });

  useEffect(() => {
    if (categories.length > 0) {
      refreshMeasurementDisplayMapFromCategories(categories);
    }
  }, [categories]);

  const categoriesByCode = useMemo(() => {
    const map: Record<string, number> = {};
    categories.forEach((c) => {
      map[c.code] = c.id;
    });
    return map;
  }, [categories]);

  const codeById = useMemo(() => {
    const map: Record<number, string> = {};
    categories.forEach((c) => {
      map[c.id] = c.code;
    });
    return map;
  }, [categories]);

  const measLabelToCategoryId = useMemo(() => buildMeasLabelToCategoryId(categories), [categories]);
  const categoryIdToMeasNames = useMemo(() => buildCategoryIdToMeasNames(categories), [categories]);
  const categoryIdToMeasLabel = useMemo(() => buildCategoryIdToMeasLabel(categories), [categories]);
  const perimeterMeasLabels = useMemo(() => buildPerimeterMeasLabels(categories), [categories]);
  const measGeometryContext = useMemo(() => {
    const ctx = buildMeasGeometryContext(categories);
    setActiveMeasGeometry(ctx);
    return ctx;
  }, [categories]);
  const labelToMeasName = useMemo(() => buildLabelToMeasName(categories), [categories]);
  const getMeasurementNameFromLabel = useCallback(
    (label: string | null | undefined) => getMeasurementNameFromLabelConfig(label, labelToMeasName),
    [labelToMeasName]
  );
  const categoryIdsWithMaxTwo = useMemo(
    () => new Set(categories.filter((c) => getMaxStructuresForCategory(c) > 1).map((c) => c.id)),
    [categories]
  );

  const getMaxStructuresForCategoryId = useCallback(
    (id: number | null | undefined) => {
      if (id == null) return 1;
      return getMaxStructuresForCategory(categories.find((c) => c.id === id));
    },
    [categories]
  );

  const colorByCode = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach((c) => {
      const fromDb = c.default_color_rgba?.trim();
      map[c.code] = fromDb || stableColorFromCode(c.code);
    });
    return map;
  }, [categories]);

  const getColorForCode = useCallback(
    (code: string | null | undefined): string => {
      const c = (code ?? '').trim().toLowerCase();
      if (!c) return 'rgba(128, 128, 128, 0.5)';
      if (colorByCode[c]) return colorByCode[c];
      return stableColorFromCode(c);
    },
    [colorByCode]
  );

  const getColorForCategoryId = useCallback(
    (id: number | null | undefined): string => {
      if (id == null) return 'rgba(128, 128, 128, 0.5)';
      const code = codeById[id];
      return code ? getColorForCode(code) : stableColorFromCode(`category_${id}`);
    },
    [codeById, getColorForCode]
  );

  const getColorForLabel = useCallback(
    (label: string | null | undefined): string => {
      const code = normalizeToCode(label);
      return getColorForCode(code || undefined);
    },
    [getColorForCode]
  );

  const value = useMemo<CategoriesContextValue>(
    () => ({
      categories,
      categoriesByCode,
      codeById,
      measLabelToCategoryId,
      categoryIdToMeasNames,
      categoryIdToMeasLabel,
      perimeterMeasLabels,
      measGeometryContext,
      categoryIdsWithMaxTwo,
      getMaxStructuresForCategoryId,
      colorByCode,
      getColorForCategoryId,
      getColorForCode,
      getColorForLabel,
      labelToMeasName,
      getMeasurementNameFromLabel,
      isLoading,
      isError,
    }),
    [
      categories,
      categoriesByCode,
      codeById,
      measLabelToCategoryId,
      categoryIdToMeasNames,
      categoryIdToMeasLabel,
      perimeterMeasLabels,
      measGeometryContext,
      categoryIdsWithMaxTwo,
      getMaxStructuresForCategoryId,
      colorByCode,
      getColorForCategoryId,
      getColorForCode,
      getColorForLabel,
      labelToMeasName,
      getMeasurementNameFromLabel,
      isLoading,
      isError,
    ]
  );

  return (
    <CategoriesContext.Provider value={value}>
      {children}
    </CategoriesContext.Provider>
  );
}

export function useCategories(): CategoriesContextValue {
  const ctx = useContext(CategoriesContext);
  if (!ctx) {
    throw new Error('useCategories must be used within CategoriesProvider');
  }
  return ctx;
}

export function useCategoriesOptional(): CategoriesContextValue | null {
  return useContext(CategoriesContext);
}
